import { Button, Select, message } from "antd";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { AppContext } from "../../context/AppContext";
import useHttp from "../../hooks/useHttp";
import useTeamsRecipients from "../../hooks/useTeamsRecipients";

const CRM_CONTACT_SEARCH_DEBOUNCE_MS = 300;

const ContactRecipients = React.memo(
    ({ toNumber, setToNumber, tabIndex, customClasses, disabled, defaultSelectFirstOption = true, setSelectedTeamsContact }) => {
        const [appProperties] = useContext(AppContext);
        const [toNumberSelectLoading, setToNumberSelectLoading] = useState(false);
        const [toPhoneNumberList, setToPhoneNumberList] = useState([]);
        const [searchResults, setSearchResults] = useState([]);
        const [searching, setSearching] = useState(false);
        const [crmSearchQuery, setCrmSearchQuery] = useState("");
        const [crmSearchHadNoMatches, setCrmSearchHadNoMatches] = useState(false);
        const abortControllerRef = useRef(null);
        const { fetchData } = useHttp();
        const { loadRecipients, searchCrmContacts } = useTeamsRecipients(appProperties, fetchData);


        useEffect(() => {
            loadRecipients(setToPhoneNumberList, setToNumber, setToNumberSelectLoading);
        }, [loadRecipients, setToNumber]);

        useEffect(() => {
            if (!defaultSelectFirstOption || !toPhoneNumberList?.length || toNumber) return;
            const first = toPhoneNumberList[0];
            if (first?.fieldValue) {
                setToNumber(first.fieldValue);
                if (setSelectedTeamsContact && first.id) {
                    setSelectedTeamsContact({ id: first.id });
                }
            }
        }, [defaultSelectFirstOption, setSelectedTeamsContact, setToNumber, toNumber, toPhoneNumberList]);

        const debouncedRunCrmSearch = useDebouncedCallback(
            (trimmedQuery) => {
                abortControllerRef.current?.abort();
                abortControllerRef.current = new AbortController();
                const signal = abortControllerRef.current.signal;
                setSearching(true);
                searchCrmContacts(trimmedQuery, signal)
                    .then((results) => {
                        const list = results || [];
                        setSearchResults(list);
                        setCrmSearchHadNoMatches(list.length === 0);
                    })
                    .catch(() => {
                        setSearchResults([]);
                        setCrmSearchHadNoMatches(false);
                    })
                    .finally(() => setSearching(false));
            },
            CRM_CONTACT_SEARCH_DEBOUNCE_MS
        );

        useEffect(() => {
            return () => {
                debouncedRunCrmSearch.cancel();
                abortControllerRef.current?.abort();
            };
        }, [debouncedRunCrmSearch]);

        const handleSearch = useCallback(
            (searchValue) => {
                const trimmed = searchValue?.trim() || "";
                if (!trimmed) {
                    debouncedRunCrmSearch.cancel();
                    abortControllerRef.current?.abort();
                    setSearchResults([]);
                    setCrmSearchQuery("");
                    setCrmSearchHadNoMatches(false);
                    setSearching(false);
                    return;
                }
                setCrmSearchQuery(trimmed);
                setCrmSearchHadNoMatches(false);
                debouncedRunCrmSearch(trimmed);
            },
            [debouncedRunCrmSearch]
        );

        const normalizePhoneNumber = useCallback((value = "") => {
            const trimmedValue = value.trim();
            const withoutInvalidChars = trimmedValue.replace(/[^\d+]/g, "");
            return withoutInvalidChars.replace(/(?!^)\+/g, "");
        }, []);

        const allOptions = useMemo(() => {
            const toOption = (item) => {
                const value = item?.fieldValue ?? item?.phone;
                const displayName =
                    (item?.fieldName ?? [item?.firstName, item?.lastName].filter(Boolean).join(" ")) || value || item?.id;
                const isOptionDisabled = item?.disabled === true;
                if (!value?.trim()) {
                    if (isOptionDisabled && (item?.id ?? item?.id)) {
                        const id = item?.id ?? item?.id;
                        return {
                            label: `${displayName} (no phone)`,
                            value: `__no_phone__:${id}`,
                            id,
                            disabled: true,
                        };
                    }
                    return null;
                }
                return {
                    label: displayName + (value ? ` (${value})` : ""),
                    value,
                    id: item?.id ?? item?.id,
                    disabled: isOptionDisabled,
                };
            };
            const previousOptions = (toPhoneNumberList || []).map(toOption).filter(Boolean);
            const seenValues = new Set(previousOptions.map((option) => option.value));
            const searchOptions = (searchResults || []).map(toOption).filter((option) => {
                if (!option || seenValues.has(option.value)) return false;
                seenValues.add(option.value);
                return true;
            });
            return [...previousOptions, ...searchOptions];
        }, [searchResults, toPhoneNumberList]);

        const normalizedCustomFromSearch = useMemo(
            () => normalizePhoneNumber(crmSearchQuery),
            [crmSearchQuery, normalizePhoneNumber]
        );
        const isValidCustomPhone = useMemo(
            () => /^\+?[1-9]\d{6,14}$/.test(normalizedCustomFromSearch),
            [normalizedCustomFromSearch]
        );

        const handleSelect = useCallback(
            (value, selectedOption) => {
                setToNumber(value);
                const id = selectedOption?.id;
                if (setSelectedTeamsContact) {
                    setSelectedTeamsContact(id ? { id } : null);
                }
            },
            [setSelectedTeamsContact, setToNumber]
        );

        const handleSendToCustomNumber = useCallback(() => {
            if (!isValidCustomPhone) {
                message.warning("Enter a valid phone number in search.");
                return;
            }
            // eslint-disable-next-line no-console -- intentional trace for custom-number sends
            console.info("[ContactRecipients] send to custom number (no CRM match)", normalizedCustomFromSearch);
            handleSelect(normalizedCustomFromSearch, {});
            setSearchResults([]);
            setCrmSearchQuery("");
            setCrmSearchHadNoMatches(false);
        }, [handleSelect, isValidCustomPhone, normalizedCustomFromSearch]);

        const showCustomNumberCta =
            !toNumberSelectLoading && !searching && crmSearchQuery && crmSearchHadNoMatches && isValidCustomPhone;
        const showNoContactsHint =
            !toNumberSelectLoading && !searching && crmSearchQuery && crmSearchHadNoMatches && !isValidCustomPhone;

        const renderNotFoundContent = () => {
            if (toNumberSelectLoading || searching) {
                return (
                    <div className="d-flex justify-content-center p-2">
                        <div className="actionIconsSprite dropDownLoading" />
                    </div>
                );
            }
            return <div className="text-center p-2">Search by phone or name to find CRM contacts.</div>;
        };

        return (
            <Select
                disabled={disabled}
                className={`rounded w-100 h-45 ${customClasses || ""}`}
                id="toPhoneNumber-teams"
                value={toNumber || undefined}
                options={allOptions}
                showSearch
                filterOption={false}
                onSearch={handleSearch}
                loading={toNumberSelectLoading || searching}
                tabIndex={tabIndex}
                dropdownRender={(menu) => (
                    <div className="dropDownList">
                        {menu}
                        {showCustomNumberCta ? (
                            <div
                                className="d-flex flex-column align-items-stretch gap-2 p-2 border-top"
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                }}
                            >
                                <div className="text-center hs-fs-12 text-muted">No contacts match your search.</div>
                                <Button type="primary" onClick={handleSendToCustomNumber}>
                                    Send to {normalizedCustomFromSearch}
                                </Button>
                            </div>
                        ) : null}
                        {showNoContactsHint ? (
                            <div
                                className="text-center p-2 border-top hs-fs-12 text-muted"
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                }}
                            >
                                No contacts found. Type a full phone number (e.g. +15551234567) to send without a
                                contact.
                            </div>
                        ) : null}
                    </div>
                )}
                onChange={(value, selectedOption) => handleSelect(value, selectedOption)}
                suffixIcon={
                    toNumberSelectLoading || searching ? (
                        <div className="actionIconsSprite dropDownLoading" />
                    ) : (
                        <div className="actionIconsSprite fromNumberDropDownIcon" />
                    )
                }
                notFoundContent={renderNotFoundContent()}
                placeholder={toNumberSelectLoading ? "" : "Search or select contact"}
            />
        );
    }
);

export default ContactRecipients;
